import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { ListView } from '../../design-system/list-view/ListView'
import type { Density, ExportFormat, SortSpec } from '../../design-system/list-view/types'
import { usePersistentState } from '../../lib/usePersistentState'
import { useSavedViews } from '../../lib/savedViews'
import { rsqlEquals } from '../../lib/rsql'
import { runExport, exportCapNote, type ExportColumn } from '../../lib/export'
import { listCases } from '../../api/cases'
import { getUiConfig } from '../../api/identity'
import { ApiError } from '../../api/problem'
import type { CaseListView } from '../../types/case'
import { CASE_COLUMN_ORDER, buildCaseColumns } from './caseColumns'

/**
 * IB-326/IB-327 — the "New Case"/"New Production" CTAs' `/me/ui-config` component keys
 * (SecurityMatrixSeeder), mirroring `TeamPanel`'s `case.team-manage` precedent exactly. This is
 * CTA-visibility convenience ONLY, not a security boundary: CaseService.create and
 * ProductionService.create currently have no server-side capability check (only
 * TenantContext.require()) -- a pre-existing platform gap tracked separately (the reverted
 * case:W check), not closed by this story. Defaults to hidden if `/me/ui-config` cannot be
 * reached.
 */
const CASE_CREATE_COMPONENT = 'case.create'
const PRODUCTION_CREATE_COMPONENT = 'film.production-create'

const PAGE_LIMIT = 50

type KindFilter = 'all' | 'rnd' | 'film'

/** AC1: an unrecognized `?kind=` value defaults to "All" — never a 500. */
function parseKind(raw: string | null): KindFilter {
  return raw === 'rnd' || raw === 'film' ? raw : 'all'
}

function buildSort(sort: SortSpec[]): string | null {
  if (sort.length === 0) return null
  return sort.map((s) => (s.direction === 'desc' ? `-${s.columnId}` : s.columnId)).join(',')
}

/** Everything a saved view needs to capture for this list (Saved Views/Filters story) — the
 * R&D / Film & Media / All toggle plus sort/columns/density (Cases has no free-text search or
 * advanced filter panel in this slice). */
interface CasesSavedViewState {
  kind: KindFilter
  sort: SortSpec[]
  columnOrder: string[]
  hiddenColumnIds: string[]
  pinnedColumnIds: string[]
  density: Density
}

/**
 * The Cases list (IB-53 Slice A AC1–3) — one list, an R&D/Film/All toggle,
 * Account · Legal entity · Case · Fiscal year columns (CLAUDE.md §4), names
 * never UUIDs (backed by the `CaseListView` projection). Replaces the
 * `/cases` `ComingSoonStub`.
 */
export function CasesListScreen() {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const kind = parseKind(searchParams.get('kind'))

  const columns = useMemo(() => buildCaseColumns(), [])
  const [columnOrder, setColumnOrder] = usePersistentState<string[]>('iw.cases.columnOrder', CASE_COLUMN_ORDER)
  const [hiddenColumnIdsArr, setHiddenColumnIdsArr] = usePersistentState<string[]>(
    'iw.cases.hiddenColumns',
    columns.filter((c) => c.defaultHidden).map((c) => c.id),
  )
  const [pinnedColumnIdsArr, setPinnedColumnIdsArr] = usePersistentState<string[]>('iw.cases.pinnedColumns', ['accountName'])
  const [density, setDensity] = usePersistentState<Density>('iw.cases.density', 'compact')

  const hiddenColumnIds = useMemo(() => new Set(hiddenColumnIdsArr), [hiddenColumnIdsArr])
  const pinnedColumnIds = useMemo(() => new Set(pinnedColumnIdsArr), [pinnedColumnIdsArr])

  const [sort, setSort] = useState<SortSpec[]>([{ columnId: 'overallStatus', direction: 'asc' }])
  const [selection, setSelection] = useState<Set<string>>(new Set())

  const [cases, setCases] = useState<CaseListView[]>([])
  const [cursor, setCursor] = useState<string | null>(null)
  const [loading, setLoading] = useState(true)
  const [loadingMore, setLoadingMore] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [canCreateCase, setCanCreateCase] = useState(false)
  const [canCreateProduction, setCanCreateProduction] = useState(false)

  // Capability-hide only (IB-326/IB-327 AC4 honesty caveat above) — independent of the main load
  // so a ui-config failure never blocks the list itself; defaults to hidden.
  useEffect(() => {
    getUiConfig()
      .then((config) => {
        setCanCreateCase(config.components.includes(CASE_CREATE_COMPONENT))
        setCanCreateProduction(config.components.includes(PRODUCTION_CREATE_COMPONENT))
      })
      .catch(() => {
        setCanCreateCase(false)
        setCanCreateProduction(false)
      })
  }, [])

  const filterString = useMemo(() => (kind === 'all' ? null : rsqlEquals('kind', kind)), [kind])
  const sortString = useMemo(() => buildSort(sort), [sort])
  const latestRequestRef = useRef(0)

  const fetchPage = useCallback(
    async (afterCursor: string | null, replace: boolean) => {
      const requestId = ++latestRequestRef.current
      try {
        if (replace) {
          setLoading(true)
          setError(null)
        } else {
          setLoadingMore(true)
        }
        const page = await listCases({ filter: filterString, sort: sortString, cursor: afterCursor, limit: PAGE_LIMIT })
        if (latestRequestRef.current !== requestId) return
        setCases((prev) => (replace ? page.items : [...prev, ...page.items]))
        setCursor(page.nextCursor)
      } catch (err) {
        if (latestRequestRef.current !== requestId) return
        setError(err instanceof ApiError ? (err.problem.detail ?? err.message) : 'Could not load cases. Check your connection and try again.')
      } finally {
        if (latestRequestRef.current === requestId) {
          setLoading(false)
          setLoadingMore(false)
        }
      }
    },
    [filterString, sortString],
  )

  useEffect(() => {
    setSelection(new Set())
    void fetchPage(null, true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterString, sortString])

  function retry() {
    void fetchPage(null, true)
  }

  const quickFilters = useMemo(
    () => [
      { id: 'all', label: 'All' },
      { id: 'rnd', label: 'R&D' },
      { id: 'film', label: 'Film & Media' },
    ],
    [],
  )
  const activeQuickFilterIds = useMemo(() => new Set([kind]), [kind])

  function onQuickFilterToggle(id: string) {
    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev)
        if (id === 'all') next.delete('kind')
        else next.set('kind', id)
        return next
      },
      { replace: true },
    )
  }

  function toggleColumnHidden(id: string) {
    setHiddenColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }
  function moveColumn(id: string, direction: -1 | 1) {
    setColumnOrder((prev) => {
      const index = prev.indexOf(id)
      const target = index + direction
      if (index === -1 || target < 0 || target >= prev.length) return prev
      const next = [...prev]
      const tmp = next[index]!
      next[index] = next[target]!
      next[target] = tmp
      return next
    })
  }
  function toggleColumnPin(id: string) {
    setPinnedColumnIdsArr((prev) => (prev.includes(id) ? prev.filter((c) => c !== id) : [...prev, id]))
  }

  const savedViewState: CasesSavedViewState = useMemo(
    () => ({ kind, sort, columnOrder, hiddenColumnIds: hiddenColumnIdsArr, pinnedColumnIds: pinnedColumnIdsArr, density }),
    [kind, sort, columnOrder, hiddenColumnIdsArr, pinnedColumnIdsArr, density],
  )
  const applySavedViewState = useCallback((state: CasesSavedViewState) => {
    onQuickFilterToggle(state.kind)
    setSort(state.sort)
    setColumnOrder(state.columnOrder)
    setHiddenColumnIdsArr(state.hiddenColumnIds)
    setPinnedColumnIdsArr(state.pinnedColumnIds)
    setDensity(state.density)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const savedViews = useSavedViews<CasesSavedViewState>('cases', savedViewState, applySavedViewState)

  // --- export (Reporting & Exports) — mirrors AccountsListScreen/FilingsListScreen's convention:
  // exports exactly the columns currently visible, in the currently-loaded rows only.
  const exportColumns: ExportColumn<CaseListView>[] = useMemo(
    () =>
      columnOrder
        .map((id) => columns.find((c) => c.id === id))
        .filter((c): c is NonNullable<typeof c> => c !== undefined && !hiddenColumnIds.has(c.id))
        .map((c) => ({ id: c.id, header: c.header, toText: c.toExportText })),
    [columnOrder, hiddenColumnIds, columns],
  )
  function handleExport(format: ExportFormat) {
    runExport(format, 'Cases', cases, exportColumns)
  }
  function handleExportSelected(selectedIds: string[]) {
    const idSet = new Set(selectedIds)
    runExport('csv', 'Cases (selected)', cases.filter((c) => idSet.has(c.id)), exportColumns)
  }

  const state: 'loading' | 'error' | 'empty-tenant' | 'empty-filtered' | 'ready' = error
    ? 'error'
    : loading
      ? 'loading'
      : cases.length === 0
        ? kind !== 'all'
          ? 'empty-filtered'
          : 'empty-tenant'
        : 'ready'

  return (
    <ListView<CaseListView>
      entityIcon="briefcase"
      title="Cases"
      helpId="list.cases"
      primaryAction={canCreateCase ? { label: 'New case', icon: 'plus', onClick: () => navigate('/cases/new') } : undefined}
      headerSecondaryActions={
        canCreateProduction ? (
          <Link to="/productions/new" className="iw-btn iw-btn--sm">
            New production
          </Link>
        ) : undefined
      }
      countLabel={loading ? undefined : `${cases.length}${cursor ? '+' : ''}`}
      quickFilters={quickFilters}
      activeQuickFilterIds={activeQuickFilterIds}
      onQuickFilterToggle={onQuickFilterToggle}
      search=""
      onSearchChange={() => {}}
      searchLabel="Search cases"
      filterChildren={<p style={{ fontSize: 'var(--iw-size-sm)', color: 'var(--iw-text-muted)' }}>Use the R&D / Film & Media toggle above to narrow this list.</p>}
      activeFilterCount={0}
      activeFilterChips={[]}
      onRemoveFilterChip={() => {}}
      onClearFilters={() => onQuickFilterToggle('all')}
      savedViews={savedViews}
      onExport={handleExport}
      exportNote={exportCapNote(cases.length, Boolean(cursor))}
      columns={columns}
      columnOrder={columnOrder}
      hiddenColumnIds={hiddenColumnIds}
      pinnedColumnIds={pinnedColumnIds}
      onToggleColumnHidden={toggleColumnHidden}
      onMoveColumn={moveColumn}
      onToggleColumnPin={toggleColumnPin}
      sort={sort}
      onSortChange={setSort}
      density={density}
      onDensityChange={setDensity}
      state={state}
      rows={cases}
      getRowId={(row) => row.id}
      onRowActivate={(row) => navigate(`/cases/${row.id}`)}
      selection={selection}
      onSelectionChange={setSelection}
      bulkActions={[{ id: 'export-selected', label: 'Export selected', icon: 'download', onRun: handleExportSelected }]}
      errorMessage={error ?? undefined}
      onRetry={retry}
      emptyTenant={{
        icon: 'briefcase',
        title: 'No cases yet',
        description: 'Cases arrive once a Legal entity or Production is opened for assessment.',
      }}
      hasMore={Boolean(cursor)}
      onLoadMore={() => void fetchPage(cursor, false)}
      loadingMore={loadingMore}
    />
  )
}
